import { Logger } from '../core/logger/Logger';
import { regionStore } from '../store/jotai/store';
import { newVersionAvailableAtom } from '../store/jotai/atoms/app-version.atom';

/**
 * Notices when the server is serving a newer build than this tab is running.
 *
 * Why this exists: there is no service worker, and `index.html` is not reliably
 * served with cache directives, so a browser can keep an old document — and
 * therefore an old bundle — for hours after a deploy. That is not cosmetic: the
 * `REACT_APP_*` values (API endpoints, the Strapi API tokens used pre-login) are
 * baked in at build time, so a stale bundle keeps presenting credentials from
 * the previous build. When those are rotated the app fails in ways that read as
 * "wrong password" rather than "old tab".
 *
 * The build stamps `__BUILD_ID__` into the bundle and the same id into
 * `version.json` (see config-overrides.js). Different id → newer deploy.
 *
 * Deliberately prompt-only: the user chooses when to reload, so this can never
 * interrupt a checkout or a half-filled form.
 */

const VERSION_URL = '/version.json';

/** Don't re-check more often than this, however many times the tab is focused. */
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

let lastCheckedAt = 0;

const currentBuildId = (): string | null =>
  typeof __BUILD_ID__ === 'undefined' ? null : __BUILD_ID__;

const fetchServedBuildId = async (): Promise<string | null> => {
  // `no-store` plus a cache-busting param: the point of the check is to defeat a
  // stale cache, so it must not be answerable from one — by the browser or by
  // anything in between.
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' }
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as { buildId?: string };

  return payload?.buildId ?? null;
};

const check = async (): Promise<void> => {
  const buildId = currentBuildId();

  // No id means jest: config-overrides.js exports the function form, which
  // react-app-rewired applies to `start` as well as `build`, so the dev server
  // does define one (and serves a matching version.json from memory — the two
  // agree for the life of the process, so this never fires there).
  if (!buildId || regionStore.get(newVersionAvailableAtom)) {
    return;
  }

  const now = Date.now();
  if (now - lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }
  lastCheckedAt = now;

  try {
    const servedBuildId = await fetchServedBuildId();

    if (servedBuildId && servedBuildId !== buildId) {
      Logger.info(`New build available (running ${buildId}, served ${servedBuildId})`);
      regionStore.set(newVersionAvailableAtom, true);
    }
  } catch (error) {
    // Offline, or version.json not deployed yet. Nothing to tell the user.
    Logger.warn('Version check failed', error);
  }
};

/**
 * Starts checking: once now, then whenever the tab is brought back to the
 * foreground (throttled). No polling timer — a backgrounded tab checking on a
 * schedule buys nothing, since the user cannot act on the prompt until they
 * look at it.
 */
const start = (): (() => void) => {
  check();

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      check();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => document.removeEventListener('visibilitychange', onVisibilityChange);
};

export const versionService = {
  start,
  _test: { check, currentBuildId }
};
